import { useMemo } from "react";
import { ProfileStatus } from "store/types/enum";
import { useOneTimePurchase } from "store/hooks/useOneTimePurchase";
import { useProjectQuota } from "hooks/useProjectQuota";
import { useProfileStats } from "hooks/useProfileStats";
import { useSessionUser } from "hooks/useSession";
import type { ProjectState } from "hooks/useProjects/useProjects.types";
import type { ProjectDataType } from "store/types/project";
import type {
  CreateProjectPermissionGuardHook,
  CreateProjectPermissionState,
} from "./CreateProjectPermissionGuard.types";

type ApiShapedDraft = Partial<ProjectDataType> & {
  isLicensed?: boolean;
  isTrial?: boolean;
  permissions?: { projectInfo?: { isLicensed?: boolean } };
  trialExpiryDate?: string;
};

export type CreateProjectGuardProjectInput = ApiShapedDraft | ProjectState | null | undefined;

const isProjectUnlicensedForGuard = (
  project: NonNullable<CreateProjectGuardProjectInput>
): boolean => {
  const p = project as Partial<ProjectDataType> & {
    isLicensed?: boolean;
    isTrial?: boolean;
    permissions?: { projectInfo?: { isLicensed?: boolean } };
  };
  if (typeof p.isLicensed === "boolean") {
    return !p.isLicensed;
  }
  if (typeof p.is_licensed === "boolean") {
    return !p.is_licensed;
  }
  if (typeof p.permissions?.projectInfo?.isLicensed === "boolean") {
    return !p.permissions.projectInfo.isLicensed;
  }
  if (typeof p.is_trial === "boolean") {
    return p.is_trial;
  }
  if (typeof p.isTrial === "boolean") {
    return p.isTrial;
  }
  return false;
};

export const useCreateProjectPermissionGuard = (
  currentProject?: CreateProjectGuardProjectInput
): CreateProjectPermissionGuardHook => {
  const { canCreateFromSubscriptionQuota, unlicensedUnderCap } = useProjectQuota();
  const { profileStatus } = useProfileStats();
  const { data: session, isLoading: isSessionLoading, isOneTimePurchase } = useSessionUser();

  const {
    isLoading: isOneTimePurchaseLoading,
    canCreateProject: canCreateProjectFromOneTimePurchase,
    availableLicenses,
  } = useOneTimePurchase();

  const isCurrentProjectUnlicensed = Boolean(
    currentProject && isProjectUnlicensedForGuard(currentProject)
  );

  const isDeactivated = profileStatus === ProfileStatus.Deactivated;
  const isSubscriptionExpired = profileStatus === ProfileStatus.SubscriptionExpired;
  const isSubscriptionUser = !isOneTimePurchase;

  const canCreateFromOneTimePurchaseWithAccess =
    canCreateProjectFromOneTimePurchase && isOneTimePurchase;

  const shouldShowCreateSheet = Boolean(
    canCreateFromSubscriptionQuota || canCreateFromOneTimePurchaseWithAccess
  );

  const shouldShowPurchaseModal = Boolean(
    isOneTimePurchase &&
    ((!canCreateFromOneTimePurchaseWithAccess && !shouldShowCreateSheet) ||
      (isCurrentProjectUnlicensed && availableLicenses === 0 && !unlicensedUnderCap))
  );

  const canCreateProject = useMemo(() => {
    if (isOneTimePurchase) {
      return Boolean(canCreateFromOneTimePurchaseWithAccess);
    }
    return Boolean(canCreateFromSubscriptionQuota);
  }, [isOneTimePurchase, canCreateFromOneTimePurchaseWithAccess, canCreateFromSubscriptionQuota]);

  const purchaseModalMode = useMemo(() => {
    if (isOneTimePurchase && isCurrentProjectUnlicensed && availableLicenses > 0) {
      return "licenseProject" as const;
    }
    return "purchase" as const;
  }, [isOneTimePurchase, isCurrentProjectUnlicensed, availableLicenses]);

  const permissions: CreateProjectPermissionState = {
    isLoading: Boolean(isSessionLoading || isOneTimePurchaseLoading),
    isDeactivated,
    isSubscriptionExpired,
    isOneTimePurchase: Boolean(isOneTimePurchase),
    isSubscriptionUser: Boolean(isSubscriptionUser),
    canCreateFromSubscriptionQuota: Boolean(canCreateFromSubscriptionQuota),
    canCreateFromOneTimePurchase: Boolean(canCreateProjectFromOneTimePurchase),
    canCreateFromOneTimePurchaseWithAccess: Boolean(canCreateFromOneTimePurchaseWithAccess),
    hasAvailableTrial: false,
    hasActiveTrial: false,
    trialDaysRemaining: 0,
    shouldShowCreateSheet,
    shouldShowPurchaseModal,
    shouldShowQuotaModal: Boolean(
      isSubscriptionUser &&
      !isDeactivated &&
      !isSubscriptionExpired &&
      !shouldShowCreateSheet &&
      !shouldShowPurchaseModal &&
      !canCreateFromSubscriptionQuota
    ),
    shouldRedirectToActivation: Boolean(isDeactivated),
    shouldRedirectToSelectPlan: Boolean(isSubscriptionExpired && isSubscriptionUser),
    purchaseModalMode: purchaseModalMode as CreateProjectPermissionState["purchaseModalMode"],
  };

  const actions = {
    handleClick: () => {
      if (permissions.isLoading) return "none";
      if (permissions.shouldRedirectToActivation) return "redirectToActivation";
      if (permissions.shouldRedirectToSelectPlan) return "redirectToSelectPlan";
      if (permissions.shouldShowPurchaseModal) return "showPurchaseModal";
      if (permissions.shouldShowCreateSheet) return "showCreateSheet";
      return "showPurchaseModal";
    },
    showCreateSheet: () => "showCreateSheet",
    showPurchaseModal: () => "showPurchaseModal",
    showQuotaModal: () => "showQuotaModal",
  };

  return {
    permissions,
    actions,
    canCreateProject,
  };
};
